import { useState, useRef, useEffect } from 'react';
import { User, Settings, LogOut, ChevronDown, BookOpen, ExternalLink, Building2 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { USER_TYPES } from '../../utils/constants';
import { getDocsUrl } from '../../utils/docsUrl';
import { authService } from '../../services/auth.service';

const UserProfileDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    // Server-side revoke: ends the VSN+ session AND calls KC admin API
    // `logoutUserSessions` for this user, so any lingering KC realm session is
    // killed without a browser redirect. Best-effort — never block signout.
    try {
      await authService.logoutVSN();
    } catch (err) {
      console.warn('VSN+ session revoke failed during logout', err);
    }

    // Clear local auth state (session storage + Zustand) and land on /login.
    logout();
    navigate('/login', { replace: true });
  };

  const getUserRole = () => {
    if (!user) return '';
    switch (user.type) {
      case USER_TYPES.SUPER_ADMIN:
        return 'SuperAdmin';
      case USER_TYPES.CHANNEL_ADMIN:
        return 'ChannelAdmin';
      case USER_TYPES.TENANT_ADMIN:
        return 'TenantAdmin';
      case USER_TYPES.USER:
        return 'MobileClient';
      default:
        return '';
    }
  };

  const getUserInitials = () => {
    const name = user?.displayName || user?.email;
    if (!name) return 'U';
    if (user?.displayName) {
      const parts = user.displayName.split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return user.displayName.charAt(0).toUpperCase();
    }
    // For email, use first char before @
    const local = name.split('@')[0];
    return local ? local.charAt(0).toUpperCase() : 'U';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
      >
        <div className="w-9 h-9 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-sm">
          {getUserInitials()}
        </div>
        <div className="text-left">
          <div className="text-sm font-semibold text-gray-900">{user?.email || user?.displayName || user?.username || 'User'}</div>
          <div className="text-xs text-gray-500">{getUserRole()}</div>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
          {/* User Info */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center space-x-2 mb-2">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                {getUserInitials()}
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">{user?.email || user?.displayName || user?.username || 'User'}</p>
                <p className="text-xs text-gray-500">{getUserRole()}</p>
              </div>
            </div>
            {user?.tenantName && (
              <p className="text-xs text-gray-400 mt-2 pt-2 border-t border-gray-100">
                <span className="font-medium text-gray-500">Tenant:</span> {user.tenantName}
              </p>
            )}
          </div>

          {/* Menu Items */}
          <div className="py-1">
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/profile');
              }}
              className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <User className="w-4 h-4" />
              <span>My Profile</span>
            </button>
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/profile/settings');
              }}
              className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Settings className="w-4 h-4" />
              <span>Account Settings</span>
            </button>
            {user?.type === USER_TYPES.TENANT_ADMIN && (
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/my-tenant');
                }}
                className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <Building2 className="w-4 h-4" />
                <span>My Tenant</span>
              </button>
            )}
          </div>

          {/* Documentation */}
          <div className="border-t border-gray-100 pt-1">
            <a
              href={getDocsUrl(pathname)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsOpen(false)}
              className="w-full flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <BookOpen className="w-4 h-4" />
                <span>Documentation</span>
              </div>
              <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
            </a>
          </div>

          {/* Logout */}
          <div className="border-t border-gray-100 pt-1">
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileDropdown;

